import type { EditorDynamicConfig } from './use-editor-setup';
import type { API } from '@editorjs/editorjs';
import type EditorJS from '@editorjs/editorjs';
import type { ChangeEvent, JSX, RefObject } from 'react';

import { Box, Button, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { toaster } from '#components/ui/toaster';

import { executeAttachUploadLinkModal } from '../tools/files/attach-upload-link-flow';
import { applyLinkUrl } from '../utils/apply-editor-link';

type EditorSelectionWithClear = API['selection'] & { clearSaved?: () => void };

/** Blocks where a non-empty `/` opens the custom slash menu (text-capable tools). */
const SLASH_MENU_BLOCK_TOOLS = new Set([
  'paragraph',
  'header',
  'list',
  'documentHeading',
  'table',
]);

/**
 * True when the rect has a real position or size. Collapsed carets often report 0×0
 * with correct `left`/`top` (use that). A fully zero rect means "no position" in
 * some browsers and would incorrectly trigger the block fallback.
 */
function hasUsableClientRect(r: DOMRect): boolean {
  return (
    r.width > 0.5 ||
    r.height > 0.5 ||
    (Number.isFinite(r.top) && Math.abs(r.top) + Math.abs(r.left) > 0.5)
  );
}

/**
 * Nearest text line / cell for the selection — critical for list blocks, where
 * the block `holder` is the whole tree (wrong bottom edge for menu placement).
 */
function getNarrowEditableRectFromSelection(sel: Selection): DOMRect | null {
  let node: Node | null = sel.anchorNode;
  if (node?.nodeType === Node.TEXT_NODE) {
    node = node.parentElement;
  }

  if (!(node instanceof Element)) {
    return null;
  }

  const narrow = node.closest(
    [
      '.cdx-list__item-content',
      '.cdx-list__item',
      '.ce-paragraph',
      '.ce-header',
      '.editorjs-document-heading__title',
      '.tc-cell',
    ].join(', '),
  );
  return narrow ? narrow.getBoundingClientRect() : null;
}

function getCaretOrBlockRect(editor: EditorJS): DOMRect {
  const sel = window.getSelection();
  if (!sel?.rangeCount) {
    return getFallbackBlockRect(editor);
  }

  const range = sel.getRangeAt(0);

  const rects = range.getClientRects();
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (hasUsableClientRect(r)) {
      return r;
    }
  }

  const bounds = range.getBoundingClientRect();
  if (hasUsableClientRect(bounds)) {
    return bounds;
  }

  const narrow = getNarrowEditableRectFromSelection(sel);
  if (narrow && hasUsableClientRect(narrow)) {
    return narrow;
  }

  return getFallbackBlockRect(editor);
}

function getFallbackBlockRect(editor: EditorJS): DOMRect {
  const idx = editor.blocks.getCurrentBlockIndex();
  const block = editor.blocks.getBlockByIndex(idx);
  if (block?.holder) {
    return block.holder.getBoundingClientRect();
  }

  return new DOMRect(8, 8, 0, 0);
}

export type EditorSlashMenuProps = {
  holderRef: RefObject<HTMLDivElement | null>;
  editorRef: RefObject<EditorJS | null>;
  dynamicConfigRef: RefObject<EditorDynamicConfig>;
  /** Enables Image (upload); mirrors whether `onUpload` is passed to the editor. */
  canUpload: boolean;
  /** Enables Link upload; mirrors whether `onRequestAttachUploadLink` is passed. */
  canAttachUploadLink: boolean;
  /** Enables Link (URL); mirrors whether `onRequestPasteEditorLink` is passed. */
  canPasteEditorLink: boolean;
};

/** Slash menu when `/` is typed in a non-empty text-capable block (native `/` stays for empty blocks). */
export function EditorSlashMenu({
  holderRef,
  editorRef,
  dynamicConfigRef,
  canUpload,
  canAttachUploadLink,
  canPasteEditorLink,
}: EditorSlashMenuProps): JSX.Element | null {
  const [menuPos, setMenuPos] = useState<{ left: number; top: number } | null>(
    null,
  );
  const menuRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const closeMenu = useCallback(() => {
    setMenuPos(null);
  }, []);

  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return;

    const onKeyDownCapture = (ev: KeyboardEvent): void => {
      if (ev.key !== '/' || ev.defaultPrevented) return;

      if (ev.ctrlKey || ev.metaKey || ev.altKey) return;

      if (ev.isComposing) return;

      const editor = editorRef.current;
      if (!editor) return;

      if (editor.readOnly.isEnabled) return;

      const idx = editor.blocks.getCurrentBlockIndex();
      const block = editor.blocks.getBlockByIndex(idx);
      if (!block) return;

      if (!SLASH_MENU_BLOCK_TOOLS.has(block.name)) {
        return;
      }

      if (block.isEmpty) {
        return;
      }

      ev.preventDefault();
      ev.stopPropagation();

      const rect = getCaretOrBlockRect(editor);
      const pad = 6;
      const left = Math.min(rect.left, window.innerWidth - 280 - pad);
      const top = Math.min(rect.bottom + 4, window.innerHeight - 200 - pad);

      setMenuPos({ left: Math.max(pad, left), top: Math.max(pad, top) });
    };

    holder.addEventListener('keydown', onKeyDownCapture, true);
    return () => holder.removeEventListener('keydown', onKeyDownCapture, true);
  }, [editorRef, holderRef]);

  useEffect(() => {
    if (!menuPos) return;

    const onDocMouseDown = (ev: MouseEvent): void => {
      const t = ev.target;
      if (!(t instanceof Node)) return;

      if (menuRef.current?.contains(t)) return;

      closeMenu();
    };

    const onKeyDown = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [closeMenu, menuPos]);

  const handleLinkUrl = useCallback(() => {
    const editor = editorRef.current;
    closeMenu();
    if (!editor) return;

    const request = dynamicConfigRef.current.onRequestPasteEditorLink;
    if (!request) {
      toaster.create({
        title: 'Add link unavailable',
        type: 'info',
      });
      return;
    }

    const selection = editor.selection as EditorSelectionWithClear;
    selection.save();

    request({
      applyLink: (href, label) => {
        void editor.focus();
        selection.restore();
        applyLinkUrl(editor, href, label);
        selection.clearSaved?.();
      },
      cancel: () => {
        selection.restore();
        selection.clearSaved?.();
      },
    });
  }, [closeMenu, dynamicConfigRef, editorRef]);

  const handleLinkUpload = useCallback(() => {
    const editor = editorRef.current;
    const config = {
      onRequestAttachUploadLink:
        dynamicConfigRef.current.onRequestAttachUploadLink,
    };
    closeMenu();
    if (!editor) return;

    const sel = window.getSelection();
    if (!sel?.rangeCount || sel.isCollapsed) {
      toaster.create({
        title: 'Select text first',
        description:
          'Choose the text to link, then use / and Link upload (or the toolbar).',
        type: 'info',
      });
      return;
    }

    if (editor.selection.findParentTag('A')) {
      toaster.create({
        title: 'Remove the existing link first.',
        type: 'info',
      });
      return;
    }

    if (!config.onRequestAttachUploadLink) {
      toaster.create({
        title: 'Link upload unavailable',
        type: 'info',
      });
      return;
    }

    executeAttachUploadLinkModal(editor as unknown as API, config);
  }, [closeMenu, dynamicConfigRef, editorRef]);

  const handlePickImage = useCallback(() => {
    closeMenu();
    fileInputRef.current?.click();
  }, [closeMenu]);

  const onFileChange = useCallback(
    async (ev: ChangeEvent<HTMLInputElement>) => {
      const file = ev.target.files?.[0];
      ev.target.value = '';
      if (!file) return;

      const upload = dynamicConfigRef.current.onUpload;
      if (!upload) {
        toaster.create({
          title: 'Image upload unavailable',
          type: 'info',
        });
        return;
      }

      const editor = editorRef.current;
      if (!editor) return;

      try {
        const url = await upload(file);
        const idx = editor.blocks.getCurrentBlockIndex();
        let data: Record<string, unknown> = { file: { url } };
        try {
          const composed = await editor.blocks.composeBlockData('image');
          data = { ...composed, file: { url } };
        } catch {
          // minimal payload is enough for @editorjs/image
        }

        editor.blocks.insert('image', data as never, undefined, idx + 1, true);
      } catch {
        toaster.create({
          title: 'Upload failed',
          type: 'error',
        });
      }
    },
    [dynamicConfigRef, editorRef],
  );

  const portal =
    menuPos &&
    createPortal(
      <Box
        ref={menuRef}
        className="editorjs-slash-menu"
        position="fixed"
        zIndex={100}
        left={`${menuPos.left}px`}
        top={`${menuPos.top}px`}
        minW="220px"
        maxW="min(280px, calc(100vw - 16px))"
      >
        <VStack align="stretch" gap={1} p={2}>
          <Button
            size="sm"
            variant="ghost"
            justifyContent="flex-start"
            fontWeight="normal"
            onClick={handlePickImage}
            disabled={!canUpload}
          >
            Image (upload)
          </Button>
          <Button
            size="sm"
            variant="ghost"
            justifyContent="flex-start"
            fontWeight="normal"
            onClick={handleLinkUrl}
            disabled={!canPasteEditorLink}
          >
            Link (URL)
          </Button>
          <Button
            size="sm"
            variant="ghost"
            justifyContent="flex-start"
            fontWeight="normal"
            onClick={handleLinkUpload}
            disabled={!canAttachUploadLink}
          >
            Link upload
          </Button>
        </VStack>
      </Box>,
      document.body,
    );

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        aria-hidden
        onChange={onFileChange}
      />
      {portal}
    </>
  );
}
